import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { MeResponse } from '@cleartask/shared';
import { api, ApiClientError, SessaoExpiradaError } from './lib/api.js';
import { AppShell } from './components/AppShell.js';
import { Logo } from './components/Logo.js';
import { ToastProvider } from './components/ui/Toast.js';
import { AutoSync } from './features/sync/AutoSync.js';
import { AtualizacaoDisponivel } from './features/pwa/AtualizacaoDisponivel.js';
import { SessaoExpirada } from './features/auth/SessaoExpirada.js';
import { GlobalShortcuts } from './features/commands/GlobalShortcuts.js';
import { useTheme } from './features/settings/useTheme.js';

/** Hook do tema pendurado num componente — precisa estar sob o SessionProvider. */
function ThemeEffect() {
  useTheme();
  return null;
}
import { Calendar } from './screens/Calendar.js';
import { Focus } from './screens/Focus.js';
import { Matrix } from './screens/Matrix.js';
import { More } from './screens/More.js';
import { Tasks } from './screens/Tasks.js';
import { SessionProvider } from './session.js';
import { useNavItems } from './navigation.js';

type Session =
  | { state: 'loading' }
  | { state: 'authenticated'; me: MeResponse }
  | { state: 'denied'; message: string }
  // Sessao do Access caiu. Sem `message`: a copy e sempre a mesma, e o que
  // importa dizer nao vem do erro — vem de saber que nada foi perdido.
  | { state: 'expired' }
  | { state: 'error'; message: string };

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Reconsultar a cada foco de janela e barulho para um app de 3 pessoas:
        // as invalidacoes explicitas apos cada mutacao ja mantem a tela correta.
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}

/**
 * A sessao e resolvida uma vez, na entrada. Se GET /me devolve 401, nao ha
 * tela de login a mostrar: quem autentica e a Cloudflare Access, na borda,
 * antes da requisicao chegar aqui (spec 2.4).
 */
export default function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' });
  // Um cliente por instancia de App, criado uma unica vez. Em escopo de modulo
  // o cache vazaria entre testes; recriado a cada render, perderia tudo.
  const [queryClient] = useState(createQueryClient);

  useEffect(() => {
    let cancelled = false;

    api
      .me()
      .then((me) => {
        if (!cancelled) setSession({ state: 'authenticated', me });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.status === 401) {
          setSession({ state: 'denied', message: err.message });
          return;
        }
        /*
          Sessao do Access expirada e um estado PROPRIO, e nao um erro generico.

          Ate 13/08/2026 caia no `error` abaixo, cuja copy manda recarregar e
          avisar o administrador — instrucao errada para a causa mais provavel
          de um app que fica dias aberto no celular. E recarregar, por acaso, e
          exatamente o que resolve: so uma navegacao de topo passa pelo fluxo do
          Access. A tela agora diz isso, em vez de deixar a pessoa descobrir.
        */
        if (err instanceof SessaoExpiradaError) {
          setSession({ state: 'expired' });
          return;
        }
        setSession({
          state: 'error',
          message:
            err instanceof Error ? err.message : 'Não foi possível falar com a API.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (session.state === 'loading') {
    return <CenteredCard>Carregando sua sessão…</CenteredCard>;
  }

  if (session.state === 'expired') {
    return (
      <CenteredCard>
        <p className="font-medium">Sua sessão expirou.</p>
        <p className="mt-2 text-sm text-text-mut">
          Entrar de novo leva você direto de volta ao app. Nada do que você fez foi perdido.
        </p>
        <button
          type="button"
          onClick={() => location.reload()}
          className="min-h-touch mt-4 w-full rounded bg-brand-blue px-3 font-medium text-white"
        >
          Entrar de novo
        </button>
      </CenteredCard>
    );
  }

  if (session.state === 'denied') {
    return (
      <CenteredCard>
        <p className="font-medium">{session.message}</p>
        <p className="mt-2 text-sm text-text-mut">
          O acesso é liberado por e-mail cadastrado. Fale com o administrador.
        </p>
      </CenteredCard>
    );
  }

  if (session.state === 'error') {
    return (
      <CenteredCard>
        <p className="text-overdue">{session.message}</p>

        {/*
          A dica muda com o ambiente.

          Ate o primeiro deploy esta copy era so a de desenvolvimento, e ela
          apareceu em PRODUCAO no primeiro teste real: quem abriu o app leu
          "suba o Worker com npm run dev:api", uma instrucao que nao significa
          nada para quem nao tem o repositorio na maquina. Estado vazio ou de
          erro tem de ser instrutivo para QUEM O LE (spec 9.5) — e em producao
          quem le nao e desenvolvedor.
        */}
        <p className="mt-2 text-sm text-text-mut">
          {import.meta.env.DEV ? (
            <>
              Em desenvolvimento, suba o Worker com <code>npm run dev:api</code>.
            </>
          ) : (
            'Recarregue a página. Se o erro continuar, avise o administrador.'
          )}
        </p>
      </CenteredCard>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider
        value={session.me}
        onUserChange={(user) =>
          setSession((atual) =>
            atual.state === 'authenticated'
              ? { state: 'authenticated', me: { ...atual.me, user } }
              : atual,
          )
        }
      >
        <ToastProvider>
          {/* Acima de tudo: um deploy que nao chega e um deploy que nao houve. */}
          <AtualizacaoDisponivel />
          {/*
            Antes do resto porque muda o que TUDO na tela significa: com a
            sessao caida, a lista e cache velho e o contador de pendencias nao
            vai baixar sozinho.
          */}
          <SessaoExpirada />

          {/* Drena a fila offline ao abrir, ao voltar a rede e ao reexibir a aba. */}
          <AutoSync />
          {/* Escreve `data-theme` no <html> a partir de `settings.theme`. */}
          <ThemeEffect />

          <BrowserRouter>
            {/*
              Dentro do router porque os atalhos navegam (`1..5`, `⌘K`), e fora
              das `Routes` porque valem em qualquer tela.
            */}
            <GlobalShortcuts />

            <Routes>
              <Route element={<AppShell user={session.me.user} />}>
                <Route index element={<AbaInicial />} />
                <Route path="/tarefas" element={<Tasks />} />
                {/*
                  A MESMA tela, com o detalhe aberto pela URL. Um endereco por
                  tarefa e o que faz a paleta ⌘K levar ao item em vez de largar
                  quem escolheu na lista, e o que torna o link colavel.
                */}
                <Route path="/tarefas/:id" element={<Tasks />} />
                <Route path="/matriz" element={<Matrix />} />
                {/* A mesma tela com o detalhe aberto pela URL, como em /tarefas. */}
                <Route path="/matriz/:id" element={<Matrix />} />
                <Route path="/calendario" element={<Calendar />} />
                <Route path="/calendario/:id" element={<Calendar />} />
                <Route path="/foco" element={<Focus />} />
                <Route path="/mais" element={<More />} />

                <Route path="*" element={<Navigate to="/tarefas" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}

function CenteredCard({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 px-6">
      <Logo height={40} />
      <div
        className="w-full rounded bg-surface p-5 text-center shadow-card"
        aria-live="polite"
      >
        {children}
      </div>
    </main>
  );
}

/**
 * `/` abre a primeira aba da barra, como no TickTick: quem pos o Calendario na
 * frente quer cair nele. O `*` continua indo para Tarefas — endereco errado nao
 * e escolha de ninguem, e ali o destino previsivel vale mais.
 */
function AbaInicial() {
  const [primeira] = useNavItems();
  return <Navigate to={primeira?.path ?? '/tarefas'} replace />;
}
